import React from "react";
import { ButtonHTMLAttributes } from "react";
import cs from "classnames";
import "./NewButton.scss";

export enum NewButtonVariant {
  Primary = "primary",
  Secondary = "secondary",
  Success = "success",
  Danger = "danger",
  Warning = "warning",
  Info = "info",
  Light = "light",
  Dark = "dark",
  Outline = "outline",
  Ghost = "ghost",
}

export enum NewButtonSize {
  Small = "small",
  Medium = "medium",
  Big = "big",
  Large = "large",
  Huge = "huge",
  ExtraLarge = "extra-large",
}

export interface INewButtonProps
  extends ButtonHTMLAttributes<HTMLButtonElement> {
  children?: React.ReactNode;
  variant?: NewButtonVariant;
  size?: NewButtonSize;
  isLoading?: boolean;
  isDisabled?: boolean;
  fullWidth?: boolean;
  rounded?: boolean;
  className?: string;
  leftIcon?: React.ReactNode;
  rightIcon?: React.ReactNode;
  onlyIcon?: boolean;
  outline?: boolean;
  marginLeft?: string | number;
  marginRight?: string | number;
  paddingLeft?: string | number;
  paddingRight?: string | number;
  isActive?: boolean;
}

const NewButton: React.FC<INewButtonProps> = ({
  children,
  variant = NewButtonVariant.Primary,
  size = NewButtonSize.Medium,
  isLoading = false,
  isDisabled = false,
  fullWidth = false,
  rounded = false,
  className = "",
  leftIcon,
  rightIcon,
  onlyIcon = false,
  outline = false,
  marginLeft,
  marginRight,
  paddingLeft,
  paddingRight,
  isActive = false,
  onClick,
  ...props
}) => {
  const baseClasses = cs("new-button", {
    "new-button--small": size === NewButtonSize.Small,
    "new-button--medium": size === NewButtonSize.Medium,
    "new-button--large": size === NewButtonSize.Large,
    "new-button--huge": size === NewButtonSize.Huge,
    "new-button--extra-large": size === NewButtonSize.ExtraLarge,

    "new-button--full-width": fullWidth,
    "new-button--only-icon": onlyIcon,
    "new-button--outline": outline,
    "new-button--active": isActive,

    "new-button--rounded-full": rounded,
    "new-button--rounded": !rounded,

    loading: isLoading || isDisabled,
  });

  const variantClasses = cs({
    "new-button--primary": variant === NewButtonVariant.Primary,
    "new-button--secondary": variant === NewButtonVariant.Secondary,
    "new-button--success": variant === NewButtonVariant.Success,
    "new-button--danger": variant === NewButtonVariant.Danger,
    "new-button--warning": variant === NewButtonVariant.Warning,
    "new-button--info": variant === NewButtonVariant.Info,
    "new-button--light": variant === NewButtonVariant.Light,
    "new-button--dark": variant === NewButtonVariant.Dark,
    "new-button--outline": variant === NewButtonVariant.Outline,
    "new-button--ghost": variant === NewButtonVariant.Ghost,
  });

  const LoadingSpinner = () => (
    <div className="new-button__spinner">
      {[...Array(12)].map((_, index) => (
        <div key={index} className="new-button__spinner-line" />
      ))}
    </div>
  );

  const customStyle: React.CSSProperties = {};
  if (marginLeft !== undefined) {
    customStyle.marginLeft =
      typeof marginLeft === "number" ? `${marginLeft}px` : marginLeft;
  }
  if (marginRight !== undefined) {
    customStyle.marginRight =
      typeof marginRight === "number" ? `${marginRight}px` : marginRight;
  }

  const leftIconStyle: React.CSSProperties = {};
  const rightIconStyle: React.CSSProperties = {};

  if (paddingLeft !== undefined) {
    leftIconStyle.marginRight =
      typeof paddingLeft === "number" ? `${paddingLeft}px` : paddingLeft;
  }
  if (paddingRight !== undefined) {
    rightIconStyle.marginLeft =
      typeof paddingRight === "number" ? `${paddingRight}px` : paddingRight;
  }

  return (
    <button
      {...props}
      className={cs(baseClasses, variantClasses, className)}
      disabled={isDisabled || isLoading}
      onClick={onClick}
      type={props.type || "button"}
      style={{ ...customStyle, ...props.style }}
    >
      {onlyIcon && (leftIcon || rightIcon) && (
        <>
          {isLoading ? (
            <LoadingSpinner />
          ) : (
            leftIcon && (
              <span
                className="new-button__icon new-button__icon--left"
                style={leftIconStyle}
              >
                {leftIcon}
              </span>
            )
          )}
          {rightIcon && (
            <span
              className="new-button__icon new-button__icon--right"
              style={rightIconStyle}
            >
              {rightIcon}
            </span>
          )}
        </>
      )}
      {!onlyIcon && (
        <>
          {isLoading ? (
            <LoadingSpinner />
          ) : (
            leftIcon && (
              <span
                className="new-button__icon new-button__icon--left"
                style={leftIconStyle}
              >
                {leftIcon}
              </span>
            )
          )}
          {children}
          {rightIcon && (
            <span
              className="new-button__icon new-button__icon--right"
              style={rightIconStyle}
            >
              {rightIcon}
            </span>
          )}
        </>
      )}
    </button>
  );
};

export default NewButton;
